import React, { useState, useEffect, useMemo } from "react";
import { useActiveCompany } from "../../../shared/context/ActiveCompanyContext.jsx";
import { useInvoices } from "../../invoices/hooks/useInvoices.js";
import { usePurchases } from "../../purchases/hooks/usePurchases.js";
import { filterPartyBills } from "../utils/ledger.js";
import { openingFor } from "../models/customer.js";
import { UNASSIGNED_COMPANY, getRecordCompanyKey } from "../../../shared/utils/companyScope.js";
import { Input } from "../../../shared/components/Input.jsx";
import { Button } from "../../../shared/components/Button.jsx";
import {
  createDefaultCustomer,
  INDIAN_STATES,
  extractStateCodeFromGstin
} from "../models/customer.js";

export function CustomerForm({ initialData = null, defaultPartyType = "customer", onSubmit, onCancel, loading = false }) {
  const [formData, setFormData] = useState(createDefaultCustomer(defaultPartyType));
  const [error, setError] = useState("");
  // Companies: a new party starts on the working company; the opening balance edited here is that company's
  const { companies, activeCompany, activeCompanyId, defaultCompany } = useActiveCompany();
  const { invoices } = useInvoices();
  const { purchases } = usePurchases();
  const [companyIds, setCompanyIds] = useState([]);

  // Bills this party already has per company (a company with bills can't be unticked)
  const billsByCompany = useMemo(() => {
    const counts = {};
    if (!initialData?.id) return counts;
    [...filterPartyBills(invoices, initialData, "sales"), ...filterPartyBills(purchases, initialData, "purchases")].forEach((b) => {
      const key = getRecordCompanyKey(b, companies);
      const id = key === UNASSIGNED_COMPANY ? defaultCompany?.id : key;
      if (id) counts[id] = (counts[id] || 0) + 1;
    });
    return counts;
  }, [initialData, invoices, purchases, companies, defaultCompany]);

  useEffect(() => {
    if (initialData) {
      setFormData({
        partyType: initialData.partyType || "customer",
        name: initialData.name || "",
        mobile: initialData.mobile || "",
        email: initialData.email || "",
        gstin: initialData.gstin || "",
        stateCode: initialData.stateCode || "33",
        state: initialData.state || "Tamil Nadu",
        city: initialData.city || "",
        billingAddress: {
          addressLine1: initialData.billingAddress?.addressLine1 || "",
          addressLine2: initialData.billingAddress?.addressLine2 || "",
          city: initialData.billingAddress?.city || initialData.city || "",
          state: initialData.billingAddress?.state || initialData.state || "Tamil Nadu",
          pincode: initialData.billingAddress?.pincode || ""
        },
        bankDetails: {
          bankName: initialData.bankDetails?.bankName || "",
          accountNumber: initialData.bankDetails?.accountNumber || "",
          ifscCode: initialData.bankDetails?.ifscCode || "",
          upiId: initialData.bankDetails?.upiId || ""
        },
        openingBalance: openingFor(initialData, activeCompanyId),
        status: initialData.status || "active"
      });
      // Old records not yet assigned work with every company
      setCompanyIds(initialData.companyIds?.length ? initialData.companyIds : companies.map((c) => c.id));
    } else {
      setFormData(createDefaultCustomer(defaultPartyType));
      setCompanyIds(activeCompanyId ? [activeCompanyId] : []);
    }
  }, [initialData, defaultPartyType]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleCompany = (id) => {
    setError("");
    if (companyIds.includes(id)) {
      if (billsByCompany[id]) {
        const name = companies.find((c) => c.id === id)?.name || "this company";
        setError(`Has ${billsByCompany[id]} ${name} bill${billsByCompany[id] === 1 ? "" : "s"} — can't remove ${name}.`);
        return;
      }
      setCompanyIds(companyIds.filter((x) => x !== id));
    } else {
      setCompanyIds([...companyIds, id]);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBankChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      bankDetails: {
        ...prev.bankDetails,
        [name]: name === "ifscCode" ? value.toUpperCase() : value
      }
    }));
  };

  const handleGstinChange = (e) => {
    const val = e.target.value.toUpperCase();
    const stateMatch = extractStateCodeFromGstin(val);
    setFormData((prev) => ({
      ...prev,
      gstin: val,
      stateCode: stateMatch.code,
      state: stateMatch.name,
      billingAddress: {
        ...prev.billingAddress,
        state: stateMatch.name,
        stateCode: stateMatch.code
      }
    }));
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      billingAddress: {
        ...prev.billingAddress,
        [name]: value
      }
    }));
  };

  const handleStateChange = (e) => {
    const code = e.target.value;
    const match = INDIAN_STATES.find((s) => s.code === code) || INDIAN_STATES[0];
    setFormData((prev) => ({
      ...prev,
      stateCode: match.code,
      state: match.name,
      billingAddress: {
        ...prev.billingAddress,
        state: match.name,
        stateCode: match.code
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError("Please provide a name.");
      return;
    }

    if (!companyIds.length) {
      setError("Tick at least one company for this party.");
      return;
    }

    setError("");
    try {
      // Opening balance is per company; the old single total moves by the same difference
      const companyOpening = Number(formData.openingBalance || 0);
      const previous = initialData ? openingFor(initialData, activeCompanyId) : 0;
      const openingBalances = { ...(initialData?.openingBalances || {}) };
      if (activeCompanyId) openingBalances[activeCompanyId] = companyOpening;
      await onSubmit({
        ...formData,
        companyIds: companies.map((c) => c.id).filter((id) => companyIds.includes(id)),
        openingBalances,
        openingBalance: Math.round((Number(initialData?.openingBalance || 0) + companyOpening - previous) * 100) / 100
      });
    } catch (err) {
      setError(err.message || "Failed to save party.");
    }
  };

  const isVendorType = formData.partyType === "vendor" || formData.partyType === "both";
  const partyLabel = formData.partyType === "vendor" ? "Vendor" : formData.partyType === "both" ? "Party" : "Customer";

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <div className="form-error" style={{ marginBottom: "1rem" }}>
          {error}
        </div>
      )}

      {/* Segmented Party Type Selector */}
      <div className="form-group" style={{ marginBottom: "1rem" }}>
        <label className="form-label" style={{ fontWeight: "600", marginBottom: "6px" }}>Party Classification</label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.5rem" }}>
          <button
            type="button"
            className={`btn btn-sm ${formData.partyType === "customer" ? "btn-primary" : "btn-secondary"}`}
            onClick={() => setFormData((p) => ({ ...p, partyType: "customer" }))}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px" }}
          >
            <span>🛒</span>
            <span>Customer</span>
          </button>
          <button
            type="button"
            className={`btn btn-sm ${formData.partyType === "vendor" ? "btn-primary" : "btn-secondary"}`}
            onClick={() => setFormData((p) => ({ ...p, partyType: "vendor" }))}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px" }}
          >
            <span>🏭</span>
            <span>Vendor</span>
          </button>
          <button
            type="button"
            className={`btn btn-sm ${formData.partyType === "both" ? "btn-primary" : "btn-secondary"}`}
            onClick={() => setFormData((p) => ({ ...p, partyType: "both" }))}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px" }}
          >
            <span>🔄</span>
            <span>Both</span>
          </button>
        </div>
      </div>

      <Input
        label={`${partyLabel} / Business Name`}
        id="cust-name"
        name="name"
        value={formData.name}
        onChange={handleChange}
        placeholder={formData.partyType === "vendor" ? "e.g. Sri Krishna Yarn Mills" : "e.g. Sree Murugan Textiles"}
        required
      />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
        <Input
          label="Mobile Phone"
          id="cust-mobile"
          name="mobile"
          type="tel"
          value={formData.mobile}
          onChange={handleChange}
          placeholder="+91 98765 43210"
        />

        <Input
          label="Email Address"
          id="cust-email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="accounts@business.com"
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: "0.75rem" }}>
        <Input
          label="GSTIN (15 Digits)"
          id="cust-gstin"
          name="gstin"
          value={formData.gstin}
          onChange={handleGstinChange}
          placeholder="33AAAAA0000A1Z5"
          maxLength={15}
        />

        <div className="form-group">
          <label htmlFor="cust-state" className="form-label">State of Supply</label>
          <select
            id="cust-state"
            value={formData.stateCode}
            onChange={handleStateChange}
            className="form-control"
          >
            {INDIAN_STATES.map((s) => (
              <option key={s.code} value={s.code}>
                {s.name} ({s.code})
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
        <Input
          label="City / Town"
          id="cust-city"
          name="city"
          value={formData.city}
          onChange={handleChange}
          placeholder="Tirupur"
        />

        <Input
          label={
            formData.partyType === "vendor"
              ? `Opening balance for ${activeCompany?.name || "this company"} (₹ to pay)`
              : formData.partyType === "both"
              ? `Opening balance for ${activeCompany?.name || "this company"} (₹)`
              : `Opening balance for ${activeCompany?.name || "this company"} (₹ to receive)`
          }
          id="cust-balance"
          name="openingBalance"
          type="number"
          step="0.01"
          value={formData.openingBalance}
          onChange={handleChange}
          placeholder="0.00"
        />
      </div>

      {/* Companies this party works with */}
      {companies.length > 1 && (
        <div className="form-group" data-testid="party-companies">
          <label className="form-label">Companies</label>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
            {companies.map((c) => {
              const ticked = companyIds.includes(c.id);
              const bills = billsByCompany[c.id] || 0;
              const locked = ticked && bills > 0; // a company with bills for this party stays
              return (
                <label
                  key={c.id}
                  title={locked ? `Has ${bills} bill${bills === 1 ? "" : "s"} in ${c.name}, so it can't be removed` : undefined}
                  style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.88rem", cursor: locked ? "not-allowed" : "pointer" }}
                >
                  <input type="checkbox" checked={ticked} disabled={locked} onChange={() => toggleCompany(c.id)} />
                  <span style={{ flex: 1, minWidth: 0 }}>{c.name}</span>
                  {bills > 0 && (
                    <span style={{ fontSize: "0.72rem", color: locked ? "var(--warning, #b45309)" : "var(--text-dim)", whiteSpace: "nowrap" }}>
                      {locked ? "🔒 " : ""}{bills} bill{bills === 1 ? "" : "s"}
                    </span>
                  )}
                </label>
              );
            })}
          </div>
          <div style={{ fontSize: "0.72rem", color: "var(--text-dim)", marginTop: "6px", lineHeight: 1.5 }}>
            The party is listed, and can be billed, only in the ticked companies.
            {Object.values(billsByCompany).some(Boolean) && (
              <><br />🔒 = this party has bills in that company, so it stays. Delete those bills first to remove the company.</>
            )}
          </div>
        </div>
      )}

      {/* Address Details */}
      <div style={{ marginTop: "0.5rem", borderTop: "1px solid var(--border-subtle)", paddingTop: "0.75rem" }}>
        <label className="form-label" style={{ color: "var(--primary)" }}>Address Details</label>
        <div className="form-group">
          <input
            type="text"
            name="addressLine1"
            value={formData.billingAddress?.addressLine1 || ""}
            onChange={handleAddressChange}
            placeholder="Address Line 1 (Street / Building)"
            className="form-control"
            style={{ marginBottom: "0.5rem" }}
          />
          <input
            type="text"
            name="pincode"
            value={formData.billingAddress?.pincode || ""}
            onChange={handleAddressChange}
            placeholder="Pincode (e.g. 641604)"
            className="form-control"
          />
        </div>
      </div>

      {/* Vendor Bank & Payment Details (Shown for Vendors or Both) */}
      {isVendorType && (
        <div style={{ marginTop: "0.75rem", borderTop: "1px solid var(--border-subtle)", paddingTop: "0.75rem" }}>
          <label className="form-label" style={{ color: "var(--primary)", display: "flex", alignItems: "center", gap: "6px" }}>
            <span>🏦</span>
            <span>Vendor Bank & Payment Details (For Payments)</span>
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: "0.75rem", marginBottom: "0.5rem" }}>
            <Input
              label="Bank Name"
              name="bankName"
              value={formData.bankDetails?.bankName || ""}
              onChange={handleBankChange}
              placeholder="e.g. HDFC Bank, SBI"
            />
            <Input
              label="IFSC Code"
              name="ifscCode"
              value={formData.bankDetails?.ifscCode || ""}
              onChange={handleBankChange}
              placeholder="HDFC0001234"
              maxLength={11}
            />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: "0.75rem" }}>
            <Input
              label="Account Number"
              name="accountNumber"
              value={formData.bankDetails?.accountNumber || ""}
              onChange={handleBankChange}
              placeholder="50100234567890"
            />
            <Input
              label="UPI ID / VPA"
              name="upiId"
              value={formData.bankDetails?.upiId || ""}
              onChange={handleBankChange}
              placeholder="vendor@okhdfcbank"
            />
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: "0.75rem", marginTop: "1.25rem" }}>
        {onCancel && (
          <Button
            type="button"
            variant="secondary"
            block
            onClick={onCancel}
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          variant="primary"
          block
          loading={loading}
        >
          {initialData ? `Update ${partyLabel}` : `Save ${partyLabel}`}
        </Button>
      </div>
    </form>
  );
}

export default CustomerForm;
